import { isBuildOutdated } from './app-update-core';

export { canApplyAppUpdate, isBuildOutdated, UPDATE_BLOCKED_SCREENS } from './app-update-core';

const RELOAD_FLAG = 'clubhouse-app-reloading';
const ATTEMPTED_KEY = 'clubhouse-update-attempted';
const UPDATE_INTERVAL_MS = 5 * 60 * 1000;

function runningBuildId() {
  if (typeof document === 'undefined') {
    return process.env.WEB_BUILD_ID ?? 'dev';
  }
  return document.documentElement.getAttribute('data-build-id') || process.env.WEB_BUILD_ID || 'dev';
}

function alreadyAttemptedThisShell() {
  try {
    return sessionStorage.getItem(ATTEMPTED_KEY) === runningBuildId();
  } catch {
    return false;
  }
}

export function applyAppUpdate(force = false) {
  if (typeof window === 'undefined') {
    return;
  }
  if (!force && alreadyAttemptedThisShell()) {
    return;
  }
  try {
    sessionStorage.setItem(ATTEMPTED_KEY, runningBuildId());
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    // Ignore storage failures in private mode.
  }
  window.location.reload();
}

async function fetchRemoteBuildId() {
  const response = await fetch('/version.json', { cache: 'no-store' });
  if (!response.ok) {
    return null;
  }
  const payload = await response.json() as { buildId?: string };
  return typeof payload.buildId === 'string' ? payload.buildId : null;
}

export function startAppUpdateWatcher(options: {
  getCanApplyUpdate: () => boolean;
  onUpdateAvailable: () => void;
}) {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  let stopped = false;
  let registration: ServiceWorkerRegistration | undefined;

  function requestReload() {
    if (stopped) {
      return;
    }
    options.onUpdateAvailable();
    if (options.getCanApplyUpdate()) {
      applyAppUpdate();
    }
  }

  async function checkForUpdate(nextRegistration?: ServiceWorkerRegistration | null) {
    if (stopped) {
      return;
    }
    const activeRegistration = nextRegistration ?? registration;
    try {
      await activeRegistration?.update();
    } catch {
      // Ignore SW update failures and still check the version file.
    }
    try {
      const remote = await fetchRemoteBuildId();
      if (isBuildOutdated(runningBuildId(), remote)) {
        requestReload();
      }
    } catch {
      // Version file is unavailable during a static deploy; try again later.
    }
  }

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate();
    }
  };

  const onControllerChange = () => {
    if (stopped) {
      return;
    }
    try {
      if (sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.removeItem(RELOAD_FLAG);
        return;
      }
    } catch {
      // Continue with the reload flow when storage is blocked.
    }
    requestReload();
  };

  window.addEventListener('focus', onVisibility);
  document.addEventListener('visibilitychange', onVisibility);
  navigator.serviceWorker?.addEventListener('controllerchange', onControllerChange);

  const interval = window.setInterval(() => {
    void checkForUpdate();
  }, UPDATE_INTERVAL_MS);

  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    void navigator.serviceWorker.register('/service-worker.js').then(reg => {
      if (!stopped) {
        registration = reg;
        void checkForUpdate(reg);
      }
    });
  } else {
    void checkForUpdate();
  }

  return () => {
    stopped = true;
    window.clearInterval(interval);
    window.removeEventListener('focus', onVisibility);
    document.removeEventListener('visibilitychange', onVisibility);
    navigator.serviceWorker?.removeEventListener('controllerchange', onControllerChange);
  };
}
